import { useRHFNestedFormContext } from '@openx/components-core/lib/Form/context/RHFNestedFormContext';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext';
import { type DealFormData, dealFormField } from '@openx/types/deal';
import { PackageFields } from '@openx/types/package';
import type { TargetingProviderConfig } from '@openx/types/targeting/targetingProviderConfig';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { hasRetargetingCapableDsp } from './hasRetargetingCapableDsp';

/**
 * Deal unique-targeting (nested under deal form): true when the deal account has
 * retargeting_enabled AND a selected DSP has enrichment_features.retargeting.
 * Package create/edit (standalone form): true when the package account has retargeting_enabled.
 */
export const useIsRetargetingAllowed: NonNullable<TargetingProviderConfig['useIsRetargetingAllowed']> = () => {
  const { parentForm, isNested } = useRHFNestedFormContext();
  const { useDemandPartnersFetch, useOnBehalfOfAccountId, useAccountOptionsFetch, onBehalfOrganization } =
    useDealContext();
  const onBehalfOfAccountId = useOnBehalfOfAccountId();
  const isDealTargeting = !!isNested && !!parentForm;

  // Only subscribe on package forms; deal nested targeting has no account_uid field.
  const observablePackageAccountUid = useWatch({
    name: PackageFields.ACCOUNT_UID,
    disabled: isDealTargeting,
  });

  const { mapById } = useDemandPartnersFetch({
    account_uid: onBehalfOfAccountId,
    enabled: isDealTargeting,
  });

  const { accounts } = useAccountOptionsFetch();

  const dealParticipants = useWatch<DealFormData, 'deal_participants'>({
    control: parentForm?.control,
    name: dealFormField.DEAL_PARTICIPANTS,
    disabled: !isDealTargeting,
  });

  return useMemo(() => {
    if (accounts.loading) {
      return false;
    }

    if (isDealTargeting) {
      const account =
        onBehalfOrganization ?? accounts.list?.find(org => org.exchange_account_network?.uid === onBehalfOfAccountId);

      if (account?.retargeting_enabled !== true) {
        return false;
      }

      return hasRetargetingCapableDsp(dealParticipants, mapById);
    }

    if (!observablePackageAccountUid) {
      return false;
    }

    const account = accounts.list?.find(org => org.exchange_account_network?.uid === observablePackageAccountUid);

    return account?.retargeting_enabled === true;
  }, [
    isDealTargeting,
    accounts.loading,
    accounts.list,
    onBehalfOrganization,
    onBehalfOfAccountId,
    observablePackageAccountUid,
    dealParticipants,
    mapById,
  ]);
};
